import { useEffect, useRef, useState } from 'react';
import { Upload, X, FileText, AlertTriangle } from 'lucide-react';
import { attachmentValidationError, ALLOWED_ATTACHMENT_EXTENSIONS } from '@/lib/attachments';

// A form control that STAGES files locally (it does not upload). The parent
// owns the File[] and uploads them after the record is created. Validates each
// file against the server's rules before staging, so oversized/wrong-type files
// are rejected up front.

interface Props {
  files:     File[];
  onChange:  (files: File[]) => void;
  label?:    string;
  disabled?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Image thumbnail that owns its object URL and revokes it on unmount. */
function Thumb({ file }: { file: File }) {
  const [url] = useState(() => URL.createObjectURL(file));
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <img src={url} alt={file.name} className="w-9 h-9 rounded object-cover flex-shrink-0" />;
}

export function StagedAttachments({ files, onChange, label = 'Attachments', disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const accepted: File[] = [];
    const errs: string[] = [];
    for (const f of Array.from(list)) {
      const err = attachmentValidationError(f);
      if (err) errs.push(err);
      else accepted.push(f);
    }
    setErrors(errs);
    if (accepted.length) onChange([...files, ...accepted]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (i: number) => onChange(files.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-slate-700">{label}</span>

      <label
        onDragOver={e => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { if (!disabled) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); } }}
        className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg px-4 py-4 transition-colors
          ${disabled ? 'opacity-50 cursor-not-allowed border-slate-200' : 'cursor-pointer border-slate-300 hover:border-brand-400 hover:bg-brand-50/30'}
          ${dragOver ? 'border-brand-400 bg-brand-50/40' : ''}`}
      >
        <Upload size={18} className="text-slate-400" />
        <span className="text-xs text-slate-500">Click or drag files to attach</span>
        <span className="text-[11px] text-slate-400">Images, PDF, Word, Excel — up to 25 MB each</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ALLOWED_ATTACHMENT_EXTENSIONS.join(',')}
          className="hidden"
          disabled={disabled}
          onChange={e => addFiles(e.target.files)}
        />
      </label>

      {errors.map((e, i) => (
        <p key={i} className="text-xs text-red-600 flex items-center gap-1.5">
          <AlertTriangle size={13} /> {e}
        </p>
      ))}

      {files.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
              {f.type.startsWith('image/')
                ? <Thumb file={f} />
                : <FileText size={18} className="text-slate-400 flex-shrink-0" />}
              <span className="text-xs text-slate-700 truncate flex-1">{f.name}</span>
              <span className="text-[11px] text-slate-400 flex-shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={disabled}
                className="text-slate-400 hover:text-red-600 flex-shrink-0"
                aria-label={`Remove ${f.name}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
